import { useState, useEffect } from 'react';
import { apiClient, Story } from '../lib/apiClient';
import { supabase } from '../lib/supabaseClient';
import ReviewStudio from './ReviewStudio';

export default function Dashboard() {
  const [stories, setStories] = useState<Story[]>([]);
  const [todayStory, setTodayStory] = useState<Story | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAge, setSelectedAge] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'catalog' | 'today' | 'review'>('today');

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [todayRes, catalogRes] = await Promise.all([
          apiClient.fetchTodayStory().catch(() => null),
          apiClient.fetchCatalog(selectedAge !== 'all' ? selectedAge : undefined).catch(() => null)
        ]);

        let loadedStories: Story[] = [];

        if (catalogRes && catalogRes.stories && catalogRes.stories.length > 0) {
          loadedStories = catalogRes.stories;
        } else {
          const { data } = await supabase
            .from('stories')
            .select('*, scenes(*)')
            .order('publish_date', { ascending: false });

          if (data && data.length > 0) {
            loadedStories = data as any;
          }
        }

        setStories(loadedStories);

        if (todayRes) {
          setTodayStory(todayRes);
        } else if (loadedStories.length > 0) {
          setTodayStory(loadedStories[0]);
        }
      } catch (e) {
        console.error('Error loading dashboard stories:', e);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [selectedAge]);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-5 bg-slate-900 border border-slate-800 rounded-xl">
          <p className="text-xs text-slate-400 uppercase font-semibold">Statut Pipeline 18h00</p>
          <p className="text-2xl font-bold text-amber-400 mt-1">
            {todayStory ? `Prêt : ${todayStory.title}` : 'Prêt pour 18h00'}
          </p>
          <p className="text-xs text-slate-500 mt-1">Date : {todayStory?.publication_date || '2026-10-23'}</p>
        </div>
        <div className="p-5 bg-slate-900 border border-slate-800 rounded-xl">
          <p className="text-xs text-slate-400 uppercase font-semibold">Serveur REST API v1</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">En Ligne (JWT Active)</p>
          <p className="text-xs text-slate-500 mt-1">Conforme story.schema.json</p>
        </div>
        <div className="p-5 bg-slate-900 border border-slate-800 rounded-xl">
          <p className="text-xs text-slate-400 uppercase font-semibold">Catalogue Contes</p>
          <p className="text-2xl font-bold text-blue-400 mt-1">{stories.length} Histoire(s)</p>
          <p className="text-xs text-slate-500 mt-1">3 Tranches d'âge (2-4, 5-8, 9-12)</p>
        </div>
      </div>

      <div className="flex border-b border-slate-800 gap-4">
        <button
          onClick={() => setActiveTab('today')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition ${
            activeTab === 'today'
              ? 'border-amber-400 text-amber-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
        >
          📖 Histoire du Jour
        </button>
        <button
          onClick={() => setActiveTab('catalog')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition ${
            activeTab === 'catalog'
              ? 'border-amber-400 text-amber-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
        >
          📚 Catalogue de l'API REST v1 ({stories.length})
        </button>
        <button
          onClick={() => setActiveTab('review')}
          className={`pb-3 px-2 text-sm font-semibold border-b-2 transition ${
            activeTab === 'review'
              ? 'border-amber-400 text-amber-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
        >
          🎧 Studio de relecture
        </button>
      </div>

      {activeTab === 'review' && <ReviewStudio />}

      {activeTab === 'today' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-6">
          <div className="flex justify-between items-start border-b border-slate-800 pb-4">
            <div>
              <span className="text-xs font-semibold px-2.5 py-1 bg-amber-950 text-amber-400 border border-amber-800/80 rounded-md">
                Publié à 18h00 — {todayStory?.publication_date || '2026-10-23'}
              </span>
              <h2 className="text-2xl font-bold text-slate-100 mt-3">{todayStory?.title || 'Chargement...'}</h2>
              <p className="text-xs text-slate-400 mt-1">
                Tranche d'âge : {todayStory?.target_age_group || '2-12'} ans • Durée : {todayStory?.reading_duration_seconds ? Math.round(todayStory.reading_duration_seconds / 60) : 0} min ({todayStory?.reading_duration_seconds || 0}s)
              </p>
            </div>
            <span className="text-sm px-3 py-1 bg-emerald-950 text-emerald-400 border border-emerald-800 rounded-full font-medium">
              Format Standard v1
            </span>
          </div>

          {todayStory && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-slate-950 p-4 rounded-lg border border-slate-800">
                  <h3 className="text-xs font-semibold uppercase text-slate-400 mb-1">Synopsis Éditorial</h3>
                  <p className="text-sm text-slate-200 leading-relaxed">{todayStory.synopsis || 'En cours de rédaction éditoriale...'}</p>
                </div>

                <div className="bg-slate-950 p-4 rounded-lg border border-slate-800">
                  <h3 className="text-xs font-semibold uppercase text-slate-400 mb-1">Morale & Message</h3>
                  <p className="text-sm text-amber-300 italic">{todayStory.moral ? `« ${todayStory.moral} »` : 'Morale à venir'}</p>
                </div>

                <div className="bg-slate-950 p-4 rounded-lg border border-slate-800">
                  <h3 className="text-xs font-semibold uppercase text-slate-400 mb-2">Configuration Audio ElevenLabs</h3>
                  <div className="text-xs space-y-1 text-slate-300">
                    <p><span className="text-slate-500">Voix :</span> {todayStory.audio?.voice_name || 'George (Narrateur Feutré)'}</p>
                    <p><span className="text-slate-500">Style :</span> {todayStory.audio?.voice_style || 'Apaisant & Doux'}</p>
                    <p><span className="text-slate-500">Tempo :</span> {todayStory.audio?.tempo_words_per_minute || 110} Mots/min</p>
                    {todayStory.audio?.background_ambience && (
                      <p><span className="text-slate-500">Ambiance :</span> {todayStory.audio.background_ambience}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-xs font-semibold uppercase text-slate-400">Scènes Découpées ({todayStory.scenes?.length || 0})</h3>
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                  {todayStory.scenes?.map((scene) => (
                    <div key={scene.scene_number} className="bg-slate-950 p-3.5 rounded-lg border border-slate-800 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-amber-400">
                          Scène {scene.scene_number} • {scene.phase}
                        </span>
                        <span className="text-xs text-slate-500">{scene.estimated_duration_seconds}s</span>
                      </div>
                      {scene.title && <h4 className="text-xs font-semibold text-slate-200">{scene.title}</h4>}
                      <p className="text-xs text-slate-300 line-clamp-3">{scene.text}</p>
                      {scene.illustration && (
                        <div className="text-[11px] text-slate-500 bg-slate-900 p-2 rounded border border-slate-800/80">
                          <span className="text-slate-400 font-medium">Prompt FR:</span> {scene.illustration.prompt_fr}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'catalog' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-2 border-b border-slate-800">
            <h2 className="text-lg font-bold text-slate-100">Dernières Histoires du Catalogue REST API</h2>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">Tranche d'âge :</span>
              <select
                value={selectedAge}
                onChange={(e) => setSelectedAge(e.target.value)}
                className="bg-slate-950 border border-slate-800 text-xs text-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-500"
              >
                <option value="all">Toutes (2-12 ans)</option>
                <option value="2-4">2-4 ans</option>
                <option value="5-8">5-8 ans</option>
                <option value="9-12">9-12 ans</option>
              </select>
            </div>
          </div>

          {loading ? (
            <p className="text-slate-400 text-sm py-4 animate-pulse">Chargement des données API...</p>
          ) : stories.length === 0 ? (
            <p className="text-slate-400 text-sm py-4">Aucune histoire trouvée dans le catalogue.</p>
          ) : (
            <div className="divide-y divide-slate-800">
              {stories.map((story) => (
                <div key={story.id} className="py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1 max-w-2xl">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-amber-300 text-base">{story.title}</h3>
                      <span className="text-[10px] px-2 py-0.5 bg-slate-800 text-amber-400 rounded border border-slate-700 font-mono">
                        {story.id}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 bg-slate-800 text-blue-300 rounded border border-slate-700 font-medium">
                        {story.target_age_group} ans
                      </span>
                    </div>
                    <p className="text-xs text-slate-300">{story.synopsis}</p>
                    <p className="text-xs text-slate-500 italic">« {story.moral} »</p>
                  </div>
                  <div className="text-right flex md:flex-col items-center md:items-end justify-between gap-1">
                    <span className="text-xs px-2.5 py-1 bg-slate-950 text-slate-300 rounded-md border border-slate-800 font-mono">
                      📅 {story.publication_date}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      ⏱️ {story.reading_duration_seconds}s • {story.scenes?.length || 0} scènes
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

